import { TaggedInFeedRefreshButton } from "components/MediaLibrary/SourceMedia/TaggedInFeed/TaggedInFeedActions";
import { GET_TAGGED_IN_MEDIA_ROUTINE } from "constants/ActionTypes";
import { useSelector } from "react-redux";
import { getLoading, getRoutinesLoading } from "selectors/commonSelectors";
import { colors } from "styles/color-tokens";
import {
  getFeedMedia,
  getSelectedIgAccount,
} from "selectors/hashtagFeedSelectors";
import { CollectionFile } from "shared/types/files";
import styled from "styled-components";
import { LoadingCover } from "ui/cover";
import { CollectionFilesList } from "../../collection-files-list";
import { PickerProps } from "../media-picker";
import { EmptyAlertWrapper, Title, TitleBar } from "../components/common";
import { feedMediaToCollectionFile } from "../helper";
import { AccountSelector } from "../components/account-selector";
import { PAGES } from "constants/MediaLibrary";
import { LoadMore } from "../components/load-more";
import { getUser } from "selectors/commonSelectors";
import { hasUgcLibrary } from "utils/feature-gates";
import { UpsellWrap, UpsellUGCCTA } from "../creator/creator-picker";
import type { ReduxRootState } from "types/redux-store";
import {
  IgReconnectNotice,
  useIgAccountNeedsReconnect,
} from "../components/ig-reconnect-notice";

export function TaggedInPicker(props: PickerProps) {
  const selectedIgAccount = useSelector(getSelectedIgAccount);
  const needsReconnect = useIgAccountNeedsReconnect(selectedIgAccount);

  const doesSelectedIgAccountHaveCommentPermission = (
    selectedIgAccount?.session?.scopes || []
  ).includes("instagram_manage_comments");

  const loading = useSelector(getLoading);
  const loadingMedia = useSelector((state: ReduxRootState) =>
    getRoutinesLoading(state, [GET_TAGGED_IN_MEDIA_ROUTINE])
  );
  const feedMedia = useSelector((state: ReduxRootState) =>
    getFeedMedia(state, PAGES.TAGGED_IN)
  );

  const files: CollectionFile[] = (feedMedia || []).map(
    feedMediaToCollectionFile
  );

  const isLoading = loading || loadingMedia;

  const user = useSelector(getUser);
  const upsell = !hasUgcLibrary(user);

  if (upsell) {
    return (
      <UpsellWrap>
        <strong className="head">
          Tagged in Post is how you find and celebrate the most dedicated
          followers and customers in your audience. Here's how it works:{" "}
        </strong>

        <strong>1. Select an account you manage</strong>

        <p>You can track up to 30 hashtags at a time</p>
        <strong>2. Choose posts to save</strong>
        <p>
          The original creators' info is automatically saved so you can pass on
          the credit
        </p>

        <strong>3. Add it to your Library</strong>
        <strong>
          4. Share it across Instagram, Facebook, Twitter, Pinterest and
          Linkedln
        </strong>
        <UpsellUGCCTA />
      </UpsellWrap>
    );
  }

  if (needsReconnect) {
    return (
      <LoadingCover isLoading={false}>
        <div className="flex items-center gap-3 my-5">
          <AccountSelector type="tagged-in" />
        </div>
        <IgReconnectNotice account={selectedIgAccount} />
      </LoadingCover>
    );
  }

  return (
    <LoadingCover isLoading={isLoading}>
      <div className="flex items-center gap-3 my-5">
        <AccountSelector type="tagged-in" />
      </div>
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
          <p className="font-inter text-sm text-neutral-600">
            Loading tagged posts...
          </p>
        </div>
      ) : !doesSelectedIgAccountHaveCommentPermission ? (
        <CommnetPermissionAlert />
      ) : selectedIgAccount && feedMedia.length < 1 ? (
        <EmptyAlert />
      ) : (
        <>
          <TitleBar>
            <Title>Tagged in posts</Title>
            <MediaInfo>
              <TaggedInFeedRefreshButton />
            </MediaInfo>
          </TitleBar>
          <CollectionFilesList
            files={files}
            selectedMediaSet={props.selectedMediaFiles}
            selectedCollection={props.selectedCollection}
            onSelect={props.handleFileAdd}
            onRemove={props.handleFileRemove}
          />
          <LoadMore page={PAGES.TAGGED_IN} />
        </>
      )}
    </LoadingCover>
  );
}

function CommnetPermissionAlert() {
  return (
    <EmptyAlertWrapper>
      <h3>
        Update your Instagram permissions to search and save posts you were
        tagged in.
      </h3>
    </EmptyAlertWrapper>
  );
}

function EmptyAlert() {
  return (
    <div className="text-center py-12 max-w-md mx-auto">
      <i className="fa-regular fa-at text-4xl text-neutral-300 mb-4"></i>
      <h3 className="font-gotham font-semibold text-lg mb-2 text-neutral-900">
        No tagged posts found
      </h3>
      <p className="font-inter text-sm text-neutral-600 mb-4">
        When an Instagram public account tags you in a post, it will appear
        here.
      </p>
      <ol className="text-left inline-block font-inter text-sm space-y-2 text-neutral-700">
        <li>1. Select an Instagram account above</li>
        <li>2. Wait for posts where you're tagged</li>
        <li>3. Click posts to select and save</li>
      </ol>
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="font-inter text-xs text-blue-700">
          <i className="fa-regular fa-circle-info mr-1"></i>
          Make sure you have permission to repost user-generated content!
        </p>
      </div>
    </div>
  );
}

const MediaInfo = styled.div`
  display: flex;
  align-items: center;

  span {
    font-size: 12px;
    display: flex;
    align-items: center;
    color: ${colors.neutral400};
  }

  .SkedIcon {
    color: ${colors.neutral800};
    font-size: 15px;
    margin-left: 10px;
  }
`;
